import { SlicerParseResult, type StoredFile } from '@deizi/shared';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { Icon } from '../../components/Icon';
import { ApiClientError, api } from '../../lib/api';
import { fileContentUrl, uploadFile } from '../../lib/files-api';
import { errorMessage } from '../../lib/messages';
import { formatGrams, formatHm, parseDecimal, parseDuration } from '../../lib/numbers';
import { useIsPhone } from '../../lib/use-media';
import type { FileDraft, PlateDraft, SourceDraft } from './draft';

type Status =
  | { kind: 'idle' }
  | { kind: 'uploading'; name: string; progress: number }
  | { kind: 'parsing'; name: string }
  | { kind: 'failed'; name: string; size: number; code: string; message: string; details: unknown };

const ACCEPT = '.3mf,.gcode,.stl';

function sizeLabel(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 1 })} MB`;
}

/** Summary of the included plates: "6h20 · 172 g · 1 placa". */
export function platesSummary(plates: PlateDraft[]): {
  time: string;
  grams: string;
  plates: string;
} {
  const included = plates.filter((p) => p.included);
  const seconds = included.reduce((n, p) => n + (parseDuration(p.time) ?? 0), 0);
  const grams = included.reduce(
    (n, p) => n + p.slots.reduce((m, s) => m + (parseDecimal(s.grams) ?? 0), 0),
    0,
  );
  return {
    time: formatHm(seconds),
    grams: formatGrams(Math.round(grams)),
    plates: included.length === 1 ? '1 placa' : `${included.length} placas`,
  };
}

function NotSlicedInfo({ details }: { details: unknown }) {
  const d = (details ?? {}) as {
    printerModel?: string | null;
    plates?: number;
    filaments?: { type?: string; color?: string | null }[];
  };
  const found = [
    d.printerModel ? `impressora ${d.printerModel}` : null,
    typeof d.plates === 'number' ? `${d.plates} ${d.plates === 1 ? 'placa' : 'placas'}` : null,
    d.filaments?.length ? `filamentos ${d.filaments.map((f) => f.type ?? '?').join(', ')}` : null,
  ].filter(Boolean);
  return found.length ? <p className="xs muted">Encontramos: {found.join(' · ')}.</p> : null;
}

/** The setup wizard's sample: a real X2D sliced file (fixtures/slicer). */
export const SAMPLE_URL = '/exemplo/suporte-celular-x2d.gcode.3mf';

export function FilePanel({
  file,
  source,
  plates,
  onLoaded,
  onClear,
  onManual,
  onPickProduct,
  sample = false,
}: {
  file: FileDraft | null;
  source: SourceDraft | null;
  plates: PlateDraft[];
  onLoaded: (file: StoredFile, result: SlicerParseResult) => void;
  onClear: () => void;
  onManual: () => void;
  onPickProduct: () => void;
  /** Setup wizard "arquivo de exemplo": loads the bundled sliced file once. */
  sample?: boolean;
}) {
  const phone = useIsPhone();
  const input = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [over, setOver] = useState(false);

  const pick = () => input.current?.click();
  const sampleLoaded = useRef(false);
  useEffect(() => {
    if (!sample || sampleLoaded.current) return;
    sampleLoaded.current = true;
    void fetch(SAMPLE_URL)
      .then((r) => r.blob())
      .then((b) => handle(new File([b], 'suporte-celular-x2d.gcode.3mf')));
    // handle is stable enough for a one-time load.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [sample]);

  async function handle(f: File) {
    setStatus({ kind: 'uploading', name: f.name, progress: 0 });
    try {
      const stored = await uploadFile(f, 'sliced', (progress) =>
        setStatus({ kind: 'uploading', name: f.name, progress }),
      );
      setStatus({ kind: 'parsing', name: f.name });
      const result = await api.post('/slicer/parse', SlicerParseResult, { fileId: stored.id });
      setStatus({ kind: 'idle' });
      onLoaded(stored, result);
    } catch (err) {
      setStatus({
        kind: 'failed',
        name: f.name,
        size: f.size,
        code: err instanceof ApiClientError ? err.code : 'internal_error',
        message: errorMessage(err),
        details: err instanceof ApiClientError ? err.details : null,
      });
    }
  }

  const hidden = (
    <input
      ref={input}
      type="file"
      accept={ACCEPT}
      hidden
      onChange={(e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (f) void handle(f);
      }}
    />
  );

  if (status.kind === 'uploading' || status.kind === 'parsing') {
    return (
      <div className="card col" style={{ gap: 12, padding: 16 }} role="status" aria-live="polite">
        {hidden}
        <div className="row" style={{ gap: 12 }}>
          <div className="ph" style={{ width: 56, height: 56, flex: '0 0 56px' }}>
            {status.kind === 'parsing' ? 'lendo' : 'enviando'}
          </div>
          <div className="grow col" style={{ gap: 6, minWidth: 0 }}>
            <span className="trunc" style={{ fontWeight: 600 }} title={status.name}>
              {status.name}
            </span>
            <div className="progress" aria-hidden="true">
              <span
                style={{
                  width: `${status.kind === 'parsing' ? 100 : Math.round(status.progress * 100)}%`,
                }}
              />
            </div>
            <p className="xs muted">
              {status.kind === 'parsing'
                ? 'Lendo tempo, gramas e placas…'
                : `Enviando… ${Math.round(status.progress * 100)}%`}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (status.kind === 'failed') {
    const stl = status.code === 'unsupported_file' && /\.stl$/i.test(status.name);
    const notSliced = status.code === 'not_sliced';
    return (
      <div className="card col" style={{ gap: 12, padding: 16, borderColor: 'var(--err)' }}>
        {hidden}
        <div className="row" style={{ gap: 10 }}>
          <div className="ph" style={{ width: 56, height: 56, flex: '0 0 56px' }}>
            {stl ? 'STL' : notSliced ? '3MF' : 'arquivo'}
          </div>
          <div className="grow" style={{ minWidth: 0 }}>
            <span
              className="trunc"
              style={{ fontWeight: 600, display: 'block' }}
              title={status.name}
            >
              {status.name}
            </span>
            <p className="sm muted">{sizeLabel(status.size)}</p>
          </div>
          <span className="badge err">Não lido</span>
        </div>
        <div className="alert err">
          <Icon name="alert" />
          <div className="sm">
            {stl ? (
              <>
                <b>Não deu para ler tempo e gramas.</b> Um STL é só o modelo; ele ainda não foi
                fatiado.
              </>
            ) : notSliced ? (
              <>
                <b>Arquivo não fatiado.</b> É o projeto do Bambu Studio, sem tempo nem gramas.
              </>
            ) : (
              <>
                <b>Não deu para ler o arquivo.</b> {status.message}
              </>
            )}
          </div>
        </div>
        {notSliced ? <NotSlicedInfo details={status.details} /> : null}
        <p className="sm">
          Fatie no Bambu Studio e envie o <b style={{ fontWeight: 600 }}>.gcode.3mf</b>, ou digite
          os números do fatiador.
        </p>
        <div className={phone ? 'col' : 'row wrap'} style={{ gap: 8 }}>
          <button type="button" className={phone ? 'btn p block' : 'btn p'} onClick={onManual}>
            Digitar gramas e tempo
          </button>
          <button
            type="button"
            className={phone ? 'btn block' : 'btn'}
            onClick={() => {
              setStatus({ kind: 'idle' });
              pick();
            }}
          >
            Escolher outro arquivo
          </button>
        </div>
      </div>
    );
  }

  if (!file && source) {
    const sum = platesSummary(plates);
    return (
      <div className="card col" style={{ gap: 12 }}>
        {hidden}
        <div className="row" style={{ gap: 12 }}>
          <div className="ph" style={{ width: 64, height: 64, flex: '0 0 64px' }}>
            produto
          </div>
          <div className="grow col" style={{ gap: 3, minWidth: 0 }}>
            <span className="trunc" style={{ fontWeight: 600 }} title={source.label}>
              {source.label}
            </span>
            <p className="sm muted">
              {sum.time} · {sum.grams} · {sum.plates} · perfil de impressão do produto
            </p>
          </div>
          <span className="badge info">Do catálogo</span>
        </div>
        <div className="row wrap" style={{ gap: 8, alignItems: 'center' }}>
          <div className="row wrap" style={{ gap: 8 }}>
            <button type="button" className="btn sm" onClick={onPickProduct}>
              Trocar produto
            </button>
            <button type="button" className="btn sm g" onClick={pick}>
              Usar um arquivo
            </button>
            <button type="button" className="btn sm g" onClick={onClear}>
              Remover
            </button>
          </div>
          <Link className="xs" to={`/produtos/${source.productId}/variacoes`}>
            Abrir produto
          </Link>
        </div>
      </div>
    );
  }

  if (file) {
    const sum = platesSummary(plates);
    const thumb = plates.find((p) => p.included && p.thumbnailFileId)?.thumbnailFileId;
    return (
      <div
        className="card col"
        style={{ gap: 12, padding: phone ? 14 : 16, borderStyle: 'dashed', borderWidth: 2 }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) void handle(f);
        }}
      >
        {hidden}
        <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
          {thumb ? (
            <img
              className="thumb"
              src={fileContentUrl(thumb)}
              alt="Miniatura da placa"
              width={phone ? 76 : 64}
              height={phone ? 76 : 64}
            />
          ) : (
            <div className="ph" style={{ width: 64, height: 64, flex: '0 0 64px' }}>
              placa
            </div>
          )}
          <div className="grow col" style={{ gap: 3, minWidth: 0 }}>
            <span className="trunc" style={{ fontWeight: 600 }} title={file.name}>
              {file.name}
            </span>
            <p className="sm muted">
              {phone
                ? `${sizeLabel(file.sizeBytes)}${file.printer ? ` · ${file.printer}` : ''}`
                : `${sum.time} · ${sum.grams} · ${sum.plates}${file.printer ? ` · ${file.printer}` : ''}`}
            </p>
          </div>
          <span className="badge ok">Lido</span>
        </div>
        {phone ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 8 }}>
            {[
              ['Tempo', sum.time],
              ['Filamento', sum.grams],
              ['Placas', String(plates.filter((p) => p.included).length)],
            ].map(([label, value]) => (
              <div key={label} className="card sunk" style={{ padding: 10, borderRadius: 12 }}>
                <p className="xs muted">{label}</p>
                <p className="fr tab" style={{ fontSize: 18 }}>
                  {value}
                </p>
              </div>
            ))}
          </div>
        ) : null}
        {file.warnings.map((w) => (
          <p key={w} className="xs" style={{ color: 'var(--warn)' }}>
            {w}
          </p>
        ))}
        <div className="row between wrap" style={{ gap: 8 }}>
          {phone ? null : (
            <p className="xs muted">Arraste outro G-code ou 3MF fatiado para trocar.</p>
          )}
          <div className="row" style={{ gap: 8 }}>
            <button type="button" className="btn sm" onClick={pick}>
              Trocar arquivo
            </button>
            <button type="button" className="btn sm g" onClick={onClear}>
              Remover
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={over ? 'dropzone over' : 'dropzone'}
      style={phone ? { padding: '40px 20px', gap: 12 } : undefined}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setOver(false);
        const f = e.dataTransfer.files[0];
        if (f) void handle(f);
      }}
    >
      {hidden}
      <Icon name="box" size={34} />
      {phone ? (
        <h2 style={{ fontSize: 20 }}>Mande o arquivo fatiado</h2>
      ) : (
        <p style={{ fontWeight: 600, color: 'var(--ink)' }}>Arraste o G-code ou 3MF fatiado</p>
      )}
      <p className="sm">
        {phone ? 'G-code ou 3MF do Bambu Studio. ' : ''}Lemos tempo, gramas por slot e placas.
        {phone ? '' : ' STL não tem esses números.'}
      </p>
      <button type="button" className={phone ? 'btn p block' : 'btn p'} onClick={pick}>
        {phone ? 'Enviar arquivo fatiado' : 'Escolher arquivo'}
      </button>
      <button type="button" className={phone ? 'btn block' : 'btn sm g'} onClick={onManual}>
        {phone ? 'Digitar à mão' : 'ou digite gramas e tempo'}
      </button>
      <button type="button" className={phone ? 'btn block' : 'btn sm g'} onClick={onPickProduct}>
        {phone ? 'Começar de um produto' : 'ou comece de um produto'}
      </button>
      {phone ? <p className="xs">STL não tem tempo nem gramas: fatie antes.</p> : null}
    </div>
  );
}
